import { useState, useMemo, useCallback } from 'react'
import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Card, CardContent } from '@shared/components/ui/card'
import { CreditCard, Copy, Check, ExternalLink } from 'lucide-react'
import type { PaymentLinkFormData } from '@shared/types/payment-links'

// ─── Types ────────────────────────────────────────────────────────────────────

interface OrderSummaryProps {
  isMobile?: boolean
  hasProducts: boolean
  total: number
  subtotal: number
  paymentFor: PaymentLinkFormData['paymentFor']
  selectedProducts: PaymentLinkFormData['selectedProducts']
  requireShippingAddress: boolean
  paymentPageTitle: string
  paymentPageDescription: string
  formatPrice: (cents: number) => string
}

interface PaymentFormProps {
  isMobile?: boolean
  hasProducts: boolean
  total: number
  paymentFor: PaymentLinkFormData['paymentFor']
  suggestPresetAmounts: boolean
  presetAmounts: string[]
  setAmountLimits: boolean
  minimumAmount: string
  maximumAmount: string
  currency: string
  requirePhoneNumber: boolean
  requireShippingAddress: boolean
  formatPrice: (cents: number) => string
  callToAction: string
}

// ─── Module-level helpers ─────────────────────────────────────────────────────

function calculateTotals(selectedProducts: PaymentLinkFormData['selectedProducts']) {
  if (!selectedProducts.length) return { subtotal: 0, tax: 0, total: 0 }
  const subtotal = selectedProducts.reduce((sum, p) => sum + p.price * p.quantity, 0)
  const tax = 0
  return { subtotal, tax, total: subtotal + tax }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function OrderSummary({
  isMobile = false,
  hasProducts,
  total,
  subtotal,
  paymentFor,
  selectedProducts,
  requireShippingAddress,
  paymentPageTitle,
  paymentPageDescription,
  formatPrice,
}: OrderSummaryProps) {
  return (
    <div className={isMobile ? 'p-3 rounded-xl bg-[#f4f6f6]' : 'p-4 rounded-2xl bg-[#f4f6f6]'}>
      <div className={isMobile ? 'space-y-2' : 'space-y-3'}>
        {/* Header */}
        <div>
          <h2 className={isMobile ? 'text-xl font-bold text-[#0b3b45]' : 'text-2xl font-bold text-[#0b3b45]'}>
            {hasProducts ? formatPrice(total) : '€0.00'}
          </h2>
          <p className={isMobile ? 'text-[10px] text-[#5c7c82] mt-0.5' : 'text-xs text-[#5c7c82] mt-1'}>
            {paymentFor === 'cause-service' ? 'Pay / Donate' : 'Payment for products'}
          </p>
        </div>

        {/* Items */}
        <div className={isMobile ? 'space-y-2' : 'space-y-2.5'}>
          {paymentFor === 'products-subscriptions' && hasProducts ? (
            selectedProducts.map((product) => (
              <div key={product.id} className={isMobile ? 'flex gap-2' : 'flex gap-2.5'}>
                <div className={isMobile
                  ? 'w-10 h-10 bg-white flex-shrink-0 overflow-hidden rounded-lg flex items-center justify-center'
                  : 'w-12 h-12 bg-white flex-shrink-0 overflow-hidden rounded-lg flex items-center justify-center'
                }>
                  <CreditCard className={isMobile ? 'w-5 h-5 text-[#5c7c82]' : 'w-6 h-6 text-[#5c7c82]'} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={isMobile
                    ? 'font-semibold text-[10px] text-[#0b3b45] truncate'
                    : 'font-semibold text-xs text-[#0b3b45] truncate'
                  }>
                    {product.name}
                  </p>
                  {product.description && (
                    <p className={isMobile ? 'text-[8px] text-[#5c7c82] truncate' : 'text-[10px] text-[#5c7c82] truncate'}>
                      {product.description}
                    </p>
                  )}
                  <div className={isMobile
                    ? 'flex items-center justify-between mt-0.5'
                    : 'flex items-center justify-between mt-1'
                  }>
                    <span className={isMobile ? 'text-[8px] text-[#5c7c82]' : 'text-[10px] text-[#5c7c82]'}>
                      Qty: {product.quantity}
                    </span>
                    <span className={isMobile
                      ? 'font-semibold text-[10px] text-[#0b3b45]'
                      : 'font-semibold text-xs text-[#0b3b45]'
                    }>
                      {formatPrice(product.price * product.quantity)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className={isMobile ? 'flex gap-2' : 'flex gap-2.5'}>
              <div className={isMobile
                ? 'w-10 h-10 bg-white flex-shrink-0 overflow-hidden rounded-lg flex items-center justify-center'
                : 'w-12 h-12 bg-white flex-shrink-0 overflow-hidden rounded-lg flex items-center justify-center'
              }>
                <CreditCard className={isMobile ? 'w-5 h-5 text-[#5c7c82]' : 'w-6 h-6 text-[#5c7c82]'} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={isMobile
                  ? 'font-semibold text-[10px] text-[#0b3b45] truncate'
                  : 'font-semibold text-xs text-[#0b3b45] truncate'
                }>
                  {paymentPageTitle || 'Name of cause or service'}
                </p>
                <p className={isMobile ? 'text-[8px] text-[#5c7c82] truncate' : 'text-[10px] text-[#5c7c82] truncate'}>
                  {paymentPageDescription || 'Description'}
                </p>
              </div>
            </div>
          )}

          {/* Totals — products only */}
          {paymentFor === 'products-subscriptions' && hasProducts && (
            <div className={isMobile
              ? 'space-y-1.5 pt-2 border-t border-[#e7ebec]'
              : 'space-y-2 pt-3 border-t border-[#e7ebec]'
            }>
              <div className={isMobile ? 'flex justify-between text-[10px]' : 'flex justify-between text-xs'}>
                <span className="text-[#5c7c82]">Subtotal</span>
                <span className="font-medium text-[#0b3b45]">{formatPrice(subtotal)}</span>
              </div>
              {requireShippingAddress && (
                <div className={isMobile ? 'flex justify-between text-[10px]' : 'flex justify-between text-xs'}>
                  <span className="text-[#5c7c82]">Shipping</span>
                  <span className="font-medium text-[#0b3b45]">€0.00</span>
                </div>
              )}
              <div className={isMobile ? 'flex justify-between text-[10px]' : 'flex justify-between text-xs'}>
                <span className="text-[#5c7c82]">Estimated taxes</span>
                <span className="font-medium text-[#0b3b45]">€0.00</span>
              </div>
              <div className={isMobile
                ? 'flex justify-between font-bold text-sm pt-1.5 border-t border-[#e7ebec]'
                : 'flex justify-between font-bold text-base pt-2 border-t border-[#e7ebec]'
              }>
                <span className="text-[#0b3b45]">Total</span>
                <span className="text-[#0b3b45]">{formatPrice(total)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function PaymentForm({
  isMobile = false,
  hasProducts,
  total,
  paymentFor,
  suggestPresetAmounts,
  presetAmounts,
  setAmountLimits,
  minimumAmount,
  maximumAmount,
  currency,
  requirePhoneNumber,
  requireShippingAddress,
  formatPrice,
  callToAction,
}: PaymentFormProps) {
  const ctaLabel = callToAction
    ? callToAction.charAt(0).toUpperCase() + callToAction.slice(1)
    : 'Pay'
  return (
    <Card
      className="shadow-lg border overflow-hidden"
      style={{ backgroundColor: '#FFFFFF', borderColor: '#e7ebec', borderRadius: '24px' }}
    >
      <CardContent className={isMobile ? 'p-3 space-y-3' : 'p-4 space-y-4'}>
        {/* Preset amounts */}
        {paymentFor === 'cause-service' && suggestPresetAmounts && presetAmounts.length > 0 && (
          <div className={isMobile ? 'space-y-2' : 'space-y-2.5'}>
            <Label className={isMobile ? 'text-[10px] font-medium text-[#0b3b45]' : 'text-xs font-medium text-[#0b3b45]'}>
              Select an amount
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {presetAmounts.map((amount, index) => (
                <button
                  key={index}
                  className={isMobile
                    ? 'h-9 rounded-lg border border-[#e7ebec] bg-white hover:bg-[#f4f6f6] text-[10px] font-semibold text-[#0b3b45] transition-colors'
                    : 'h-10 rounded-lg border border-[#e7ebec] bg-white hover:bg-[#f4f6f6] text-xs font-semibold text-[#0b3b45] transition-colors'
                  }
                >
                  {formatPrice(parseFloat(amount) * 100)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Custom amount */}
        {paymentFor === 'cause-service' && setAmountLimits && (
          <div className={isMobile ? 'space-y-1' : 'space-y-1.5'}>
            <Label className={isMobile ? 'text-[10px] font-medium text-[#0b3b45]' : 'text-xs font-medium text-[#0b3b45]'}>
              Amount ({currency})
            </Label>
            <Input
              type="number"
              placeholder={`${minimumAmount || '0.00'} - ${maximumAmount || 'No limit'}`}
              className={isMobile ? 'h-8 text-[10px] border-[#e7ebec]' : 'h-9 text-xs border-[#e7ebec]'}
            />
            {(minimumAmount || maximumAmount) && (
              <p className={isMobile ? 'text-[8px] text-[#5c7c82]' : 'text-[9px] text-[#5c7c82]'}>
                {minimumAmount && maximumAmount
                  ? `Enter between ${formatPrice(parseFloat(minimumAmount) * 100)} and ${formatPrice(parseFloat(maximumAmount) * 100)}`
                  : minimumAmount
                  ? `Minimum ${formatPrice(parseFloat(minimumAmount) * 100)}`
                  : `Maximum ${formatPrice(parseFloat(maximumAmount) * 100)}`
                }
              </p>
            )}
          </div>
        )}

        {/* Payment buttons */}
        <div className={isMobile ? 'space-y-3' : 'space-y-4'}>
          <div className="flex gap-2">
            <button className={isMobile
              ? 'flex-1 h-8 bg-black rounded-full text-white text-[10px] font-semibold flex items-center justify-center'
              : 'flex-1 h-9 bg-black rounded-full text-white text-xs font-semibold flex items-center justify-center'
            }>
              Pay
            </button>
            <button className={isMobile
              ? 'flex-1 h-8 bg-black rounded-full text-white text-[10px] font-semibold flex items-center justify-center'
              : 'flex-1 h-9 bg-black rounded-full text-white text-xs font-semibold flex items-center justify-center'
            }>
              <span className="flex items-center gap-1">
                <svg className={isMobile ? 'w-3 h-3' : 'w-3.5 h-3.5'} viewBox="0 0 25 10" fill="none">
                  <path d="M9.5 4.8c-.3 0-.5-.2-.5-.5V1.9c0-.3.2-.5.5-.5s.5.2.5.5v2.4c0 .3-.2.5-.5.5z" fill="white"/>
                </svg>
                Pay
              </span>
            </button>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex-1 border-t border-[#e7ebec]" />
            <span className={isMobile ? 'text-[9px] text-[#5c7c82] font-medium' : 'text-[10px] text-[#5c7c82] font-medium'}>OR</span>
            <div className="flex-1 border-t border-[#e7ebec]" />
          </div>
        </div>

        {/* Card form */}
        <div className={isMobile ? 'space-y-3' : 'space-y-4'}>
          <div className={isMobile ? 'space-y-1' : 'space-y-1.5'}>
            <Label className={isMobile ? 'text-[10px] font-medium text-[#0b3b45]' : 'text-xs font-medium text-[#0b3b45]'}>Email</Label>
            <Input
              placeholder="Email address"
              className={isMobile ? 'h-8 text-[10px] border-[#e7ebec]' : 'h-9 text-xs border-[#e7ebec]'}
            />
          </div>

          <div className={isMobile ? 'space-y-1' : 'space-y-1.5'}>
            <Label className={isMobile ? 'text-[10px] font-medium text-[#0b3b45]' : 'text-xs font-medium text-[#0b3b45]'}>Card information</Label>
            <Input placeholder="Card number" className={isMobile ? 'h-8 text-[10px] border-[#e7ebec]' : 'h-9 text-xs border-[#e7ebec]'} />
            <Input placeholder="Cardholder name" className={isMobile ? 'h-8 text-[10px] border-[#e7ebec]' : 'h-9 text-xs border-[#e7ebec]'} />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="MM/YY" className={isMobile ? 'h-8 text-[10px] border-[#e7ebec]' : 'h-9 text-xs border-[#e7ebec]'} />
              <Input placeholder="CVC" className={isMobile ? 'h-8 text-[10px] border-[#e7ebec]' : 'h-9 text-xs border-[#e7ebec]'} />
            </div>
          </div>

          <div className={isMobile ? 'space-y-1' : 'space-y-1.5'}>
            <Label className={isMobile ? 'text-[10px] font-medium text-[#0b3b45]' : 'text-xs font-medium text-[#0b3b45]'}>Country or region</Label>
            <Input placeholder="Bulgaria" className={isMobile ? 'h-8 text-[10px] border-[#e7ebec]' : 'h-9 text-xs border-[#e7ebec]'} />
          </div>

          {requirePhoneNumber && (
            <div className={isMobile ? 'space-y-1' : 'space-y-1.5'}>
              <Label className={isMobile ? 'text-[10px] font-medium text-[#0b3b45]' : 'text-xs font-medium text-[#0b3b45]'}>Phone number</Label>
              <Input placeholder="+1 (555) 000-0000" className={isMobile ? 'h-8 text-[10px] border-[#e7ebec]' : 'h-9 text-xs border-[#e7ebec]'} />
            </div>
          )}

          {requireShippingAddress && (
            <div className={isMobile ? 'space-y-1' : 'space-y-1.5'}>
              <Label className={isMobile ? 'text-[10px] font-medium text-[#0b3b45]' : 'text-xs font-medium text-[#0b3b45]'}>Shipping address</Label>
              <Input placeholder="Address" className={isMobile ? 'h-8 text-[10px] border-[#e7ebec]' : 'h-9 text-xs border-[#e7ebec]'} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="City" className={isMobile ? 'h-8 text-[10px] border-[#e7ebec]' : 'h-9 text-xs border-[#e7ebec]'} />
                <Input placeholder="ZIP" className={isMobile ? 'h-8 text-[10px] border-[#e7ebec]' : 'h-9 text-xs border-[#e7ebec]'} />
              </div>
            </div>
          )}

          <Button className={isMobile
            ? 'w-full h-9 bg-[#0b3b45] text-white rounded-xl font-semibold text-[10px] shadow-md'
            : 'w-full h-10 bg-[#0b3b45] text-white rounded-xl font-semibold text-xs shadow-md'
          }>
            {ctaLabel} {hasProducts ? formatPrice(total) : '€0.00'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

interface PaymentLinkPreviewProps {
  formData: PaymentLinkFormData
  isMobileView?: boolean
}

export function PaymentLinkPreview({ formData, isMobileView = false }: PaymentLinkPreviewProps) {
  const [copied, setCopied] = useState(false)

  const paymentUrl = `https://vinr.com/pay/${formData.shortCode || 'your-link'}`

  // Memoized formatter — rebuilt only when currency changes
  const formatter = useMemo(
    () => new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: formData.currency,
      minimumFractionDigits: 2,
    }),
    [formData.currency]
  )
  const formatPrice = useCallback((cents: number) => formatter.format(cents / 100), [formatter])

  const hasProducts = formData.selectedProducts.length > 0
  const { subtotal, total } = useMemo(
    () => calculateTotals(formData.selectedProducts),
    [formData.selectedProducts]
  )

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(paymentUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard not available
    }
  }

  const handleOpen = () => {
    window.open(paymentUrl, '_blank', 'noopener,noreferrer')
  }

  const orderSummaryProps: OrderSummaryProps = {
    hasProducts,
    total,
    subtotal,
    paymentFor: formData.paymentFor,
    selectedProducts: formData.selectedProducts,
    requireShippingAddress: formData.requireShippingAddress,
    paymentPageTitle: formData.paymentPage.title,
    paymentPageDescription: formData.paymentPage.description,
    formatPrice,
  }

  const paymentFormProps: PaymentFormProps = {
    hasProducts,
    total,
    paymentFor: formData.paymentFor,
    suggestPresetAmounts: formData.suggestPresetAmounts,
    presetAmounts: formData.presetAmounts,
    setAmountLimits: formData.setAmountLimits,
    minimumAmount: formData.minimumAmount,
    maximumAmount: formData.maximumAmount,
    currency: formData.currency,
    requirePhoneNumber: formData.requirePhoneNumber,
    requireShippingAddress: formData.requireShippingAddress,
    formatPrice,
    callToAction: formData.callToAction,
  }

  return isMobileView ? (
    // Mobile View — iPhone mockup
    // scale-75 with origin-top: collapses unused bottom layout space via marginBottom
    <div className="relative scale-75 origin-top" style={{ marginBottom: '-163px' }}>
      <div className="bg-gray-900 rounded-[2.5rem] p-2.5 shadow-2xl">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-5 bg-gray-900 rounded-b-2xl z-10" />

        <div className="bg-white rounded-[2rem] overflow-hidden w-[340px] h-[640px] relative flex flex-col">
          {/* Status bar */}
          <div className="bg-white px-5 pt-1.5 pb-1 flex justify-between items-center text-[10px] shrink-0">
            <span className="font-semibold">9:41</span>
            <div className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
              <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 01.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.242 0 1 1 0 01-1.415-1.415 5 5 0 017.072 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              <svg className="w-5 h-2.5" fill="currentColor" viewBox="0 0 24 12">
                <rect x="0" y="0" width="18" height="12" rx="2" opacity="0.35"/>
                <rect x="0" y="0" width="14" height="12" rx="2"/>
                <rect x="20" y="3" width="4" height="6" rx="1"/>
              </svg>
            </div>
          </div>

          {/* Browser URL bar */}
          <div className="bg-white border-b border-gray-100 px-2.5 py-1.5 flex items-center gap-1.5 shrink-0">
            <svg className="w-2.5 h-2.5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="flex-1 text-[9px] text-gray-500 truncate font-mono">
              vinr.com/pay/{formData.shortCode || 'your-link'}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
              title="Copy URL"
            >
              {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
            </button>
            <button
              type="button"
              onClick={handleOpen}
              className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 bg-muted overflow-hidden">
            <div className="px-2.5 py-2.5 space-y-2.5 h-full flex flex-col">
              <div className="flex-shrink-0">
                <OrderSummary isMobile={true} {...orderSummaryProps} />
              </div>
              <div className="flex-1 overflow-y-auto">
                <PaymentForm isMobile={true} {...paymentFormProps} />
              </div>
            </div>
          </div>
        </div>

        {/* Home indicator */}
        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-28 h-1 bg-gray-800 rounded-full" />
      </div>
    </div>
  ) : (
    // Desktop View
    <div className="bg-muted p-4">
      <div className="flex items-stretch">
        <div className="w-[240px] bg-white/10 flex-shrink-0">
          <div className="p-4">
            <OrderSummary {...orderSummaryProps} />
          </div>
        </div>
        <div className="flex-1 bg-white">
          <PaymentForm {...paymentFormProps} />
        </div>
      </div>
    </div>
  )
}
